// Endpoints RTK Query da feature goals (Metas — RF-17/18/19). Tipos MANUAIS
// espelhando o contrato do back (GoalsModule); NÃO regenerar openapi.
// Convenções do contrato: `month` sempre "YYYY-MM"; valores SEMPRE em centavos
// (Int). A meta é mensal; a semanal (`weekTargetValueCents`) vem derivada do back
// (mensal ÷ 4). Percentuais, "faltam", ritmo e semanal-qtd o front calcula.
import { api } from '@/lib/api';
import type { AvatarTone } from '@/components/ui/Avatar';

/** Status de um mês no histórico. `none` = sem meta definida no período. */
export type GoalStatus = 'running' | 'hit' | 'miss' | 'none';

/**
 * Enum de cor da loja como o back devolve em GET /goals/store (design system
 * fechado — NÃO é hex). Diferente de `GET /stores` / `management`, que já
 * entregam hex. Use `storeColorToHex` antes de aplicar em CSS.
 */
export type StoreColorEnum = 'GREEN' | 'PURPLE' | 'INDIGO' | 'ORANGE' | 'ROSE' | 'CYAN';

/** Espelha STORE_COLOR_TO_HEX do back (stores.dto.ts). CYAN→azul, o hex aceito. */
const STORE_COLOR_HEX: Record<StoreColorEnum, string> = {
  GREEN: '#25D366',
  PURPLE: '#7C3AED',
  INDIGO: '#3B82F6',
  ORANGE: '#F59E0B',
  ROSE: '#EC4899',
  CYAN: '#3B82F6',
};

/** Enum StoreColor → hex. Fallback GREEN pra valores inesperados. */
export function storeColorToHex(color: string): string {
  return STORE_COLOR_HEX[color as StoreColorEnum] ?? STORE_COLOR_HEX.GREEN;
}

// ─── Escritas (RF-17 / RF-18) ────────────────────────────────────────────────

export interface PutStoreGoalArgs {
  storeId: string;
  month: string;
  valueCents: number;
  qty: number;
  /** Optimistic locking (readme §19) — mandar a version lida p/ detectar 409. */
  version?: number;
}

export interface PutSellerGoalArgs {
  userId: string;
  month: string;
  valueCents: number;
  qty: number;
  version?: number;
}

/** Retorno das mutations de upsert (store/seller). */
export interface GoalMutationResult {
  month: string;
  targetValueCents: number;
  targetQty: number;
  weekTargetValueCents: number;
  updatedById: string | null;
  updatedAt: string | null;
  version: number;
  /** presente no retorno de store */
  storeId?: string;
  /** presente no retorno de seller */
  userId?: string;
}

// ─── Leituras de configuração ────────────────────────────────────────────────

export interface StoreGoalRow {
  storeId: string;
  name: string;
  /** Enum StoreColor cru ("GREEN"|"BLUE"|…). Converta com `storeColorToHex`. */
  color: StoreColorEnum;
  month: string;
  targetValueCents: number;
  targetQty: number;
  weekTargetValueCents: number;
  updatedById: string | null;
  updatedAt: string | null;
  version: number;
}

export interface StoreGoalsListDto {
  stores: StoreGoalRow[];
}

export interface SellerGoalRow {
  userId: string;
  name: string;
  avatarTone: AvatarTone;
  /** Só indica se há foto; a UI usa iniciais+tom (não monta URL assinada). */
  avatarKey: string | null;
  month: string;
  targetValueCents: number;
  targetQty: number;
  weekTargetValueCents: number;
  updatedById: string | null;
  updatedAt: string | null;
  version: number;
}

export interface SellerGoalsListDto {
  storeId: string;
  sellers: SellerGoalRow[];
}

// ─── Progresso (RF-19) ───────────────────────────────────────────────────────

export interface ProgressVendor {
  userId: string;
  name: string;
  avatarTone: AvatarTone;
  avatarKey: string | null;
  targetValueCents: number;
  targetQty: number;
  realizedValueCents: number;
  realizedQty: number;
}

export interface StoreProgressDto {
  storeId: string;
  month: string;
  targetValueCents: number;
  targetQty: number;
  realizedValueCents: number;
  realizedQty: number;
  weekTargetValueCents: number;
  weekRealizedValueCents: number;
  currentDay: number;
  daysInMonth: number;
  currentWeek: number;
  weeksInMonth: number;
  vendors: ProgressVendor[];
}

/** Progresso pessoal do vendedor logado (sem MinRole — qualquer papel). */
export interface MeProgressDto {
  userId: string;
  month: string;
  targetValueCents: number;
  targetQty: number;
  realizedValueCents: number;
  realizedQty: number;
  weekTargetValueCents: number;
  weekRealizedValueCents: number;
  currentDay: number;
  daysInMonth: number;
  currentWeek: number;
  weeksInMonth: number;
}

// ─── Histórico + auditoria ───────────────────────────────────────────────────

export interface StoreHistoryMonth {
  month: string;
  targetValueCents: number;
  targetQty: number;
  realizedValueCents: number;
  realizedQty: number;
  status: GoalStatus;
}

export interface StoreHistoryDto {
  storeId: string;
  /** Mais recente → mais antigo. */
  months: StoreHistoryMonth[];
}

export interface SellerHistoryRow {
  userId: string;
  name: string;
  avatarTone: AvatarTone;
  avatarKey: string | null;
  targetValueCents: number;
  targetQty: number;
  realizedValueCents: number;
  realizedQty: number;
  status: GoalStatus;
}

export interface SellerHistoryDto {
  storeId: string;
  month: string;
  sellers: SellerHistoryRow[];
}

export interface AuditSnapshot {
  targetValueCents: number;
  targetQty: number;
}

export interface AuditEntry {
  id: string;
  at: string;
  entity: 'StoreGoal' | 'SellerGoal';
  targetName: string;
  byId: string | null;
  byName: string;
  /** `null` quando é definição inicial (sem valor anterior). */
  old: AuditSnapshot | null;
  new: AuditSnapshot | null;
}

export interface AuditDto {
  storeId: string;
  entries: AuditEntry[];
}

// ─── Args ────────────────────────────────────────────────────────────────────

export interface StoreGoalsArgs {
  month: string;
}
export interface SellerGoalsArgs {
  storeId: string;
  month: string;
}
export interface StoreProgressArgs {
  storeId: string;
  month: string;
}
export interface MeProgressArgs {
  month: string;
}
export interface StoreHistoryArgs {
  storeId: string;
  months?: number;
}
export interface SellerHistoryArgs {
  storeId: string;
  month: string;
}
export interface AuditArgs {
  storeId: string;
  limit?: number;
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

export const goalsApi = api.injectEndpoints({
  endpoints: (build) => ({
    storeGoals: build.query<StoreGoalsListDto, StoreGoalsArgs>({
      query: ({ month }) => `/goals/store${qs({ month })}`,
      providesTags: ['Goal'],
    }),
    sellerGoals: build.query<SellerGoalsListDto, SellerGoalsArgs>({
      query: ({ storeId, month }) => `/goals/seller${qs({ storeId, month })}`,
      providesTags: ['Goal'],
    }),
    storeProgress: build.query<StoreProgressDto, StoreProgressArgs>({
      query: ({ storeId, month }) => `/goals/progress${qs({ storeId, month })}`,
      providesTags: ['Goal'],
    }),
    meProgress: build.query<MeProgressDto, MeProgressArgs>({
      query: ({ month }) => `/goals/me/progress${qs({ month })}`,
      providesTags: ['Goal'],
    }),
    storeHistory: build.query<StoreHistoryDto, StoreHistoryArgs>({
      query: ({ storeId, months }) => `/goals/history/store${qs({ storeId, months })}`,
      providesTags: ['Goal'],
    }),
    sellerHistory: build.query<SellerHistoryDto, SellerHistoryArgs>({
      query: ({ storeId, month }) => `/goals/history/seller${qs({ storeId, month })}`,
      providesTags: ['Goal'],
    }),
    goalsAudit: build.query<AuditDto, AuditArgs>({
      query: ({ storeId, limit }) => `/goals/audit${qs({ storeId, limit })}`,
      providesTags: ['Goal'],
    }),
    putStoreGoal: build.mutation<GoalMutationResult, PutStoreGoalArgs>({
      query: (body) => ({ url: '/goals/store', method: 'PUT', body }),
      invalidatesTags: ['Goal'],
    }),
    putSellerGoal: build.mutation<GoalMutationResult, PutSellerGoalArgs>({
      query: (body) => ({ url: '/goals/seller', method: 'PUT', body }),
      invalidatesTags: ['Goal'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useStoreGoalsQuery,
  useSellerGoalsQuery,
  useStoreProgressQuery,
  useMeProgressQuery,
  useStoreHistoryQuery,
  useSellerHistoryQuery,
  useGoalsAuditQuery,
  usePutStoreGoalMutation,
  usePutSellerGoalMutation,
} = goalsApi;
